/**
 * AdminDbPools (T8.3 + UX: kto w której bazie + współrzędne połączenia).
 *
 * Scope F8: read-only listing + edit `max_tenants` (low-risk UPDATE).
 * Rozwinięcie wiersza puli → panel: współrzędne połączenia (BEZ hasła — sekret
 * zostaje w skarbcu serwera) + lista tenantów siedzących w tej bazie. Pod tabelą
 * osobna sekcja baz dedykowanych (tenant z własną bazą, nie w puli).
 *
 * Model filtrowania „jak w katalogu modulow" (poprawki-develop): ZERO gornej
 * wyszukiwarki — cale filtrowanie zyje w naglowkach kolumn (wspoldzielony
 * ConfigurableTable, client-side na pobranej liscie). Kolumny wolnotekstowe
 * (Nazwa/Host) maja szukajke; kolumny liczbowe i data — bez filtra. Nad tabela
 * zostaje tylko przycisk „+ Dodaj pool" (deferred F15).
 *
 * Deferred do F15 (R2): POST create pool, DELETE pool, PATCH host/port.
 *
 * Plan: docs/phases/PHASE_8_PLAN.md §T8.3.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Database } from 'lucide-react';

import { AdminEditPoolModal } from '@/components/AdminEditPoolModal';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';

export interface PoolTenant {
  id: string;
  slug: string;
  name: string;
  code: string;
  schemaName: string | null;
  status: string;
}

export interface DedicatedTenant {
  id: string;
  slug: string;
  name: string;
  code: string;
  dbName: string | null;
  status: string;
}

export interface DbPoolListItem {
  id: string;
  name: string;
  dbName: string;
  dbHost: string | null;
  maxTenants: number;
  currentTenantCount: number;
  status: string;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  /** Tenanci siedzący w tej bazie (puli). Może brakować w starszej odpowiedzi → []. */
  tenants?: PoolTenant[];
}

interface DbConnection {
  host: string | null;
  port: string | null;
  user: string | null;
}

interface DbPoolsResponse {
  pools: DbPoolListItem[];
  dedicated?: DedicatedTenant[];
  connection?: DbConnection;
}

function utilizationPercent(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.round((current / max) * 100);
}

function utilizationColor(pct: number): 'green' | 'amber' | 'red' {
  if (pct < 70) return 'green';
  if (pct <= 90) return 'amber';
  return 'red';
}

const BAR_BG: Record<'green' | 'amber' | 'red', string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

/** Wiersz „pole: wartość" we współrzędnych połączenia. */
function ConnRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-28 shrink-0 text-xs text-text-tertiary">{label}</span>
      <span className="font-mono text-xs text-text-primary">{value}</span>
    </div>
  );
}

export function AdminDbPools() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editTarget, setEditTarget] = useState<DbPoolListItem | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'db-pools', 'list'],
    queryFn: async (): Promise<DbPoolsResponse> => {
      const res = await api.get<DbPoolsResponse>('/admin/db-pools');
      return res.data;
    },
  });

  const onMutationSuccess = () => {
    setEditTarget(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'db-pools'] });
  };

  const f15Tooltip = t('admin.dbPools.deferredToF15');
  const connection = query.data?.connection;
  const dedicated = query.data?.dedicated ?? [];

  const columns = useMemo<ColumnDefinition<DbPoolListItem>[]>(
    () => [
      {
        id: 'expand',
        // Kolumna kontrolna (chevron) — bez sortowania i bez filtra.
        accessor: () => '',
        header: '',
        sortable: false,
        filterable: false,
        hideable: false,
        renderCell: (_value, row) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedId((cur) => (cur === row.id ? null : row.id));
            }}
            aria-expanded={expandedId === row.id}
            aria-label={t('admin.dbPools.expandAria', { name: row.name })}
            className="rounded p-1 text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary"
            data-testid={`expand-pool-${row.name}`}
          >
            {expandedId === row.id ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ),
      },
      {
        id: 'name',
        accessor: 'name',
        header: t('admin.dbPools.columns.name'),
        sortable: true,
        // Wolnotekstowa — domyslna szukajka w naglowku.
        renderCell: (value) => <span className="font-mono text-xs">{String(value)}</span>,
      },
      {
        id: 'dbHost',
        // Surowa wartosc (raw) — szukajka substring po hoscie.
        accessor: (row) => row.dbHost ?? '',
        header: t('admin.dbPools.columns.dbHost'),
        sortable: true,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-tertiary">{row.dbHost ?? '—'}</span>
        ),
      },
      {
        id: 'maxTenants',
        accessor: (row) => row.maxTenants,
        header: t('admin.dbPools.columns.maxTenants'),
        sortable: true,
        // Liczba — bez szukajki, wyrownanie do prawej.
        filterable: false,
        align: 'right',
      },
      {
        id: 'currentTenantCount',
        accessor: (row) => row.currentTenantCount,
        header: t('admin.dbPools.columns.currentTenantCount'),
        sortable: true,
        filterable: false,
        align: 'right',
      },
      {
        id: 'utilization',
        accessor: (row) => utilizationPercent(row.currentTenantCount, row.maxTenants),
        header: t('admin.dbPools.columns.utilization'),
        // Pasek wykorzystania — liczba, bez szukajki.
        filterable: false,
        renderCell: (_value, row) => {
          const pct = utilizationPercent(row.currentTenantCount, row.maxTenants);
          const color = utilizationColor(pct);
          return (
            <div className="flex items-center gap-2">
              <div className="relative h-2 w-24 overflow-hidden rounded-full bg-bg-tertiary">
                <div
                  data-testid={`utilization-bar-${row.name}`}
                  className={`absolute inset-y-0 left-0 ${BAR_BG[color]}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <span data-testid={`utilization-${row.name}`} className="text-xs">
                {pct}%
              </span>
            </div>
          );
        },
      },
      {
        id: 'createdAt',
        accessor: 'createdAt',
        header: t('admin.dbPools.columns.createdAt'),
        sortable: true,
        // Data — filtr zakresu dat lub pojedynczej daty (kontrolka daty ConfigurableTable).
        headerFilter: { category: 'date' },
        renderCell: (_value, row) => (
          <span className="text-xs text-text-tertiary">
            {new Date(row.createdAt).toLocaleDateString('pl-PL')}
          </span>
        ),
      },
    ],
    [t, expandedId],
  );

  const renderActions = (row: DbPoolListItem) => (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setEditTarget(row);
        }}
        data-testid={`edit-pool-btn-${row.name}`}
      >
        {t('admin.dbPools.actions.edit')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled
        title={f15Tooltip}
        data-testid={`delete-pool-btn-${row.name}`}
      >
        {t('admin.dbPools.actions.delete')}
      </Button>
    </div>
  );

  const expandedPool = query.data?.pools.find((p) => p.id === expandedId) ?? null;

  return (
    <Layout title={t('admin.dbPools.title')}>
      <section className="px-6 py-8 flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">{t('admin.dbPools.title')}</h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('admin.dbPools.subtitle')}</p>
          </div>
          <Button disabled title={f15Tooltip} data-testid="add-pool-btn">
            {t('admin.dbPools.addButton')}
          </Button>
        </div>

        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.dbPools.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void query.refetch()}
            >
              {t('admin.dbPools.retry')}
            </Button>
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<DbPoolListItem>
                tableId="admin-db-pools"
                columns={columns}
                data={query.data?.pools ?? []}
                getRowId={(row) => row.id}
                renderActions={renderActions}
                actionsColumnLabel={t('admin.dbPools.columns.actions')}
                onRowClick={(row) => setExpandedId((cur) => (cur === row.id ? null : row.id))}
                rowClassName={(row) =>
                  row.id === expandedId ? 'bg-accent-50 dark:bg-accent-950/30' : ''
                }
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={Database}
                emptyTitle={t('admin.dbPools.empty')}
                emptyDescription=""
              />
            </PreviewTenantAuthBoundary>

            {/* Panel rozwiniętej puli: współrzędne połączenia + kto siedzi w tej bazie. */}
            {expandedPool && (
              <div
                className="rounded-xl border border-border-secondary bg-bg-elevated p-5"
                data-testid={`pool-detail-${expandedPool.name}`}
              >
                <h3 className="text-sm font-semibold text-text-primary">
                  {t('admin.dbPools.detail.title', { name: expandedPool.name })}
                </h3>

                <div className="mt-3 grid gap-6 md:grid-cols-2">
                  {/* Współrzędne połączenia (bez hasła). */}
                  <div className="flex flex-col gap-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                      {t('admin.dbPools.detail.connectionTitle')}
                    </p>
                    <ConnRow
                      label={t('admin.dbPools.detail.server')}
                      value={expandedPool.dbHost ?? connection?.host ?? '—'}
                    />
                    <ConnRow
                      label={t('admin.dbPools.detail.port')}
                      value={connection?.port ?? '—'}
                    />
                    <ConnRow
                      label={t('admin.dbPools.detail.database')}
                      value={expandedPool.dbName}
                    />
                    <ConnRow
                      label={t('admin.dbPools.detail.user')}
                      value={connection?.user ?? '—'}
                    />
                    <p className="mt-1 text-xs text-text-muted">
                      🔒 {t('admin.dbPools.detail.passwordNote')}
                    </p>
                  </div>

                  {/* Tenanci w tej bazie. */}
                  <div className="flex flex-col gap-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                      {t('admin.dbPools.detail.tenantsTitle', {
                        count: expandedPool.tenants?.length ?? 0,
                      })}
                    </p>
                    {(expandedPool.tenants?.length ?? 0) === 0 ? (
                      <p className="text-xs text-text-muted">
                        {t('admin.dbPools.detail.tenantsEmpty')}
                      </p>
                    ) : (
                      <ul className="flex flex-col divide-y divide-border-secondary">
                        {expandedPool.tenants!.map((tn) => (
                          <li
                            key={tn.id}
                            className="flex items-center justify-between gap-2 py-1.5"
                            data-testid={`pool-tenant-${tn.code}`}
                          >
                            <span className="truncate text-sm text-text-primary">{tn.name}</span>
                            <span className="font-mono text-xs text-text-tertiary">
                              {tn.schemaName ?? `tenant_${tn.code}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bazy dedykowane — tenant z własną bazą (poza pulami). */}
            <div className="mt-2">
              <h3 className="text-sm font-semibold text-text-primary">
                {t('admin.dbPools.dedicated.title')}
              </h3>
              <p className="mt-0.5 text-xs text-text-tertiary">
                {t('admin.dbPools.dedicated.subtitle')}
              </p>
              {dedicated.length === 0 ? (
                <p className="mt-3 text-xs text-text-muted">{t('admin.dbPools.dedicated.empty')}</p>
              ) : (
                <ul
                  className="mt-3 flex flex-col divide-y divide-border-secondary rounded-xl border border-border-secondary bg-bg-elevated"
                  data-testid="dedicated-list"
                >
                  {dedicated.map((d) => (
                    <li
                      key={d.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
                      data-testid={`dedicated-${d.code}`}
                    >
                      <span className="text-sm text-text-primary">{d.name}</span>
                      <span className="flex items-center gap-4 font-mono text-xs text-text-tertiary">
                        <span>
                          {t('admin.dbPools.detail.server')}: {connection?.host ?? '—'}
                        </span>
                        <span>
                          {t('admin.dbPools.detail.database')}: {d.dbName ?? '—'}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {editTarget && (
          <AdminEditPoolModal
            pool={editTarget}
            onClose={() => setEditTarget(null)}
            onSuccess={onMutationSuccess}
          />
        )}
      </section>
    </Layout>
  );
}
